import type { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs';

import { LOADER_KEY, SKIP_LOADER } from '@core/api/http-context.tokens';
import { LoadingService } from '@core/loading/loading.service';

/**
 * FIRST in the chain, and that position is load-bearing.
 *
 * Being outermost means one start/stop pair brackets the WHOLE request
 * lifetime, including the auth interceptor's 401 refresh-and-retry. If loading
 * sat after auth, the retry would look like a second request: the counter
 * would drop to zero mid-flight and the bar would blink off and on again while
 * the user waited.
 *
 * `finalize` rather than a tap/catchError pair, so the counter is released on
 * success, on error AND on unsubscribe - a cancelled navigation with withFetch
 * genuinely aborts the request, and a leaked count would pin the bar on
 * forever.
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_LOADER)) return next(req);

  const loading = inject(LoadingService);
  const key = req.context.get(LOADER_KEY);

  loading.start(key);
  return next(req).pipe(finalize(() => loading.stop(key)));
};
